import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { integrations, WITHHELD_BECAUSE, WITHHELD_LABEL } from '@cwos/api-client';
import type { integrations as Api } from '@cwos/api-client';
import { Refused } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { ListState } from '../ListStates.js';
import type { ListPhase } from '../listState.js';
import {
  rejectedDetail,
  rejectedDetailHeading,
  rejectedDetailSentence,
  rejectedRecordsPhase,
} from './integrationsLogic.js';

type RejectedRecordReport = Api.RejectedRecordReport;

/**
 * WHICH records an import left behind (F31).
 *
 * `GET /integrations/:id/history/:runId/rejected-records` was built, tested
 * end-to-end and never called by anything. So the import history told an
 * operator "14 records could not be read and were not imported" and the only
 * way to learn WHICH fourteen was to call the API by hand — which, for the
 * person this was built for, is most of the way to not having it.
 *
 * THE VENDOR RECORD ID IS CUSTOMER DATA. `externalId` names a specific client
 * encounter in the source EHR. It is rendered into this table and nowhere else:
 * it is never logged, never put in a URL (the request is keyed by integration
 * and run id), never sent to analytics, and never written into a document
 * title. The react-query key carries the two uuids and no record identity.
 *
 * THREE STATES THIS SCREEN MUST KEEP APART, and none of them is "an empty
 * table" (see `rejectedDetail` and docs/withholding-contract.md):
 *
 *   expired   the run left records behind and we can no longer say which
 *   sample    some of them are listed, and the list says it is only some
 *   refused   the reader may not see record identities at all
 *
 * The fourth, `none`, is the only one entitled to say nothing was left behind.
 */

/** The reason a reader without `integrations:manage` meets, worded once. */
export const REJECTED_REFUSAL_MESSAGE =
  `This needs a permission your account does not hold: ` +
  `${WITHHELD_BECAUSE.rejectedRecordIdentity}. You can see that this import left records ` +
  `behind, and how many. Which records is withheld — the list is not empty, and asking again ` +
  `cannot change the answer.`;

/**
 * The panel, given a phase and a report. Presentational on purpose: every
 * sentence below is derived from a SERVER FIELD, so a test can drive
 * `detailAvailable` / `detailTruncated` and watch the rendering move rather
 * than pinning the literal output of one branch.
 */
export function RejectedRecordsList({
  phase,
  report,
  onRetry,
  formatDate,
}: {
  phase: ListPhase;
  report: RejectedRecordReport | undefined;
  onRetry?: () => void;
  /** Screens pass a locale formatter; the default keeps the copy testable. */
  formatDate?: (iso: string) => string;
}) {
  const detail = report ? rejectedDetail(report) : null;

  return (
    <ListState
      phase={phase}
      surface="table"
      rows={3}
      failedMessage="We couldn’t load which records this import left behind. The count above still stands."
      onRetry={onRetry}
      refused={<Refused heading={WITHHELD_LABEL} message={REJECTED_REFUSAL_MESSAGE} />}
      // An empty `records` is NOT an empty state: `detailAvailable` decides
      // whether it means "nothing was left behind" or "we no longer keep which".
      empty={detail ? <RejectedNote detail={detail} formatDate={formatDate} /> : null}
    >
      {detail ? (
        <>
          <RejectedNote detail={detail} formatDate={formatDate} />
          <RejectedTable records={report?.records ?? []} />
        </>
      ) : null}
    </ListState>
  );
}

function RejectedNote({
  detail,
  formatDate,
}: {
  detail: ReturnType<typeof rejectedDetail>;
  formatDate?: (iso: string) => string;
}) {
  // `sample` and `expired` are the two a reader will otherwise misread, so they
  // get the alerting tone; `complete` and `none` are ordinary answers.
  const loud = detail.kind === 'sample' || detail.kind === 'expired';
  return (
    <div
      role="status"
      style={{
        marginTop: 'var(--space-8)',
        paddingLeft: 'var(--space-10)',
        borderLeft: `3px solid ${loud ? 'var(--color-look-deep)' : 'var(--color-line)'}`,
      }}
    >
      <span style={{ fontWeight: 600, fontSize: 13 }}>{rejectedDetailHeading(detail)}</span>
      <p className="rn-muted" style={{ margin: 'var(--space-4) 0 0', fontSize: 13 }}>
        {formatDate ? rejectedDetailSentence(detail, formatDate) : rejectedDetailSentence(detail)}
      </p>
    </div>
  );
}

function RejectedTable({ records }: { records: readonly Api.RejectedRecord[] }) {
  return (
    <ul
      style={{
        listStyle: 'none',
        margin: 'var(--space-10) 0 0',
        padding: 0,
        display: 'grid',
        gap: 'var(--space-6)',
      }}
    >
      {records.map((record, index) => (
        <li
          key={`${record.externalId ?? 'no-id'}-${record.field}-${index}`}
          style={{
            borderTop: '1px solid var(--color-line)',
            paddingTop: 'var(--space-6)',
            fontSize: 13,
          }}
        >
          <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>
            {record.externalId ?? (
              // A record that arrived with no readable id. An ordinary absence
              // — the source sent none — and not a withholding.
              <span className="rn-muted">The record arrived with no id</span>
            )}
          </span>
          <span className="rn-muted">
            {' '}
            · {record.field} · {record.reason}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The container: gates on the reader's own permission set, and does not fetch
 * vendor record ids into a browser until somebody asks for them.
 *
 * The gate is read HERE from `usePermissions` rather than threaded down as a
 * prop, so there is no boolean a component can be left holding after the wiring
 * that computed it is removed.
 */
export function RejectedRecordsPanel({
  integrationId,
  runId,
}: {
  integrationId: string;
  runId: string;
}) {
  const permissions = usePermissions();
  const gate = {
    canManage: permissions.has('integrations:manage'),
    isLoading: permissions.isLoading,
    isError: permissions.isError,
  };
  const [open, setOpen] = useState(false);

  const query = useQuery({
    queryKey: ['integrations', 'rejected', integrationId, runId],
    queryFn: () => integrations.rejectedRecords(integrationId, runId),
    enabled: open && gate.canManage,
  });

  const report = query.data;
  const phase = rejectedRecordsPhase(gate, query, report);

  // The disclosure exists to keep customer record ids out of the browser until
  // they are wanted — never to hide a refusal behind a click. A reader who may
  // not see them meets the refusal without asking for it.
  if (gate.canManage && !gate.isLoading && !gate.isError && !open) {
    return (
      <button
        type="button"
        className="rn-btn-quiet"
        style={{ marginTop: 'var(--space-8)' }}
        onClick={() => setOpen(true)}
      >
        Which records?
      </button>
    );
  }

  return (
    <RejectedRecordsList
      phase={phase}
      report={report}
      onRetry={() => {
        if (gate.isError) permissions.refetch();
        else void query.refetch();
      }}
      formatDate={(iso) => new Date(iso).toLocaleDateString()}
    />
  );
}
